/* eslint-disable @typescript-eslint/no-explicit-any */
import createAuth0Client, {
  Auth0Client,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  LogoutOptions,
  RedirectLoginOptions,
  User,
} from '@auth0/auth0-spa-js'
import { App, Plugin, computed, reactive, watchEffect } from 'vue'
import { NavigationGuardWithThis } from 'vue-router'

let client: Auth0Client
let backendUrl: string

interface Auth0PluginState {
  loading: boolean
  isAuthenticated: boolean
  user: User | undefined
  popupOpen: boolean
  error: any
  impersonating: boolean
  impersonationTarget: User | undefined
  impersonationToken: string | null
}

const state = reactive<Auth0PluginState>({
  loading: true,
  isAuthenticated: false,
  user: {},
  popupOpen: false,
  error: null,
  impersonating: false,
  impersonationTarget: undefined,
  impersonationToken: null,
})

async function handleRedirectCallback() {
  state.loading = true

  try {
    await client.handleRedirectCallback()
    state.user = await client.getUser()
    state.isAuthenticated = true
  } catch (e) {
    state.error = e
  } finally {
    state.loading = false
  }
}

function loginWithRedirect(o: RedirectLoginOptions) {
  return client.loginWithRedirect(o)
}

function getIdTokenClaims(o: GetIdTokenClaimsOptions) {
  return client.getIdTokenClaims(o)
}

function getTokenSilently(o: GetTokenSilentlyOptions) {
  return client.getTokenSilently(o)
}

function getTokenWithPopup(o: GetTokenWithPopupOptions) {
  return client.getTokenWithPopup(o)
}

function logout(o: LogoutOptions) {
  return client.logout(o)
}

// Impersonation functions
async function startImpersonation(targetUserId: string, durationHours = 1) {
  try {
    const idToken = await client.getIdTokenClaims()
    if (!idToken) throw new Error('No ID token available')

    const response = await fetch(`${backendUrl}/v1/admin/impersonate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken.__raw}`
      },
      body: JSON.stringify({
        targetUserId,
        durationHours
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to start impersonation')
    }

    const data = await response.json()
    
    // Store impersonation state
    state.impersonating = true
    state.impersonationTarget = data.targetUser
    state.impersonationToken = data.sessionToken
    
    // Store token in localStorage for persistence
    localStorage.setItem('impersonationToken', data.sessionToken)
    
    return data
  } catch (error) {
    console.error('Failed to start impersonation:', error)
    throw error
  }
}

async function stopImpersonation() {
  try {
    const idToken = await client.getIdTokenClaims()
    if (!idToken) throw new Error('No ID token available')

    const response = await fetch(`${backendUrl}/v1/admin/impersonate`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken.__raw}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to stop impersonation')
    }

    // Clear impersonation state
    state.impersonating = false
    state.impersonationTarget = undefined
    state.impersonationToken = null
    
    // Remove token from localStorage
    localStorage.removeItem('impersonationToken')
    
    return true
  } catch (error) {
    console.error('Failed to stop impersonation:', error)
    throw error
  }
}

async function checkImpersonationStatus() {
  try {
    const impersonationToken = localStorage.getItem('impersonationToken')
    if (!impersonationToken) {
      state.impersonating = false
      state.impersonationTarget = undefined
      state.impersonationToken = null
      return
    }

    const idToken = await client.getIdTokenClaims()
    if (!idToken) {
      state.impersonating = false
      state.impersonationTarget = undefined
      state.impersonationToken = null
      localStorage.removeItem('impersonationToken')
      return
    }

    const response = await fetch(`${backendUrl}/v1/admin/impersonation-status`, {
      headers: {
        'Authorization': `Bearer ${idToken.__raw}`
      }
    })

    if (!response.ok) {
      state.impersonating = false
      state.impersonationTarget = undefined
      state.impersonationToken = null
      localStorage.removeItem('impersonationToken')
      return
    }

    const data = await response.json()
    
    if (data.isImpersonating && data.session) {
      state.impersonating = true
      state.impersonationTarget = data.session.targetUser
      state.impersonationToken = data.session.sessionToken
    } else {
      state.impersonating = false
      state.impersonationTarget = undefined
      state.impersonationToken = null
      localStorage.removeItem('impersonationToken')
    }
  } catch (error) {
    console.error('Failed to check impersonation status:', error)
    state.impersonating = false
    state.impersonationTarget = undefined
    state.impersonationToken = null
    localStorage.removeItem('impersonationToken')
  }
}

// Get effective user (considering impersonation)
const getEffectiveUser = computed(() => {
  return state.impersonating ? state.impersonationTarget : state.user
})

// Enhanced token methods that include impersonation token
async function getIdTokenClaimsWithImpersonation(o: GetIdTokenClaimsOptions) {
  const claims = await client.getIdTokenClaims(o)
  if (state.impersonating && state.impersonationToken) {
    // Add impersonation context to headers
    return {
      ...claims,
      __impersonationToken: state.impersonationToken
    }
  }
  return claims
}

const authPlugin = {
  isAuthenticated: computed(() => state.isAuthenticated),
  loading: computed(() => state.loading),
  user: computed(() => state.user),
  impersonating: computed(() => state.impersonating),
  impersonationTarget: computed(() => state.impersonationTarget),
  effectiveUser: getEffectiveUser,
  getIdTokenClaims,
  getIdTokenClaimsWithImpersonation,
  getTokenSilently,
  getTokenWithPopup,
  handleRedirectCallback,
  loginWithRedirect,
  logout,
  startImpersonation,
  stopImpersonation,
  checkImpersonationStatus,
}

export type AuthPlugin = typeof authPlugin

const routeGuard: NavigationGuardWithThis<undefined> = (to: any, from: any, next: any) => {
  const { isAuthenticated, loading, loginWithRedirect } = authPlugin

  const verify = async () => {
    // If the user is authenticated, continue with the route
    if (isAuthenticated.value) {
      return next()
    }

    // Otherwise, log in
    await loginWithRedirect({ appState: { targetUrl: to.fullPath } })
  }

  // If loading has already finished, check our auth state using `fn()`
  if (!loading.value) {
    return verify()
  }

  // Watch for the loading property to change before we check isAuthenticated
  watchEffect(() => {
    if (!loading.value) {
      return verify()
    }
  })
}

interface Auth0PluginOptions {
  domain: string
  clientId: string
  redirectUri: string
  backendUrl: string

  onRedirectCallback(appState: any): void
}

async function init(options: Auth0PluginOptions): Promise<Plugin> {
  backendUrl = options.backendUrl
  
  client = await createAuth0Client({
    // domain: process.env.VUE_APP_AUTH0_DOMAIN,
    // client_id: process.env.VUE_APP_AUTH0_CLIENT_KEY,
    domain: options.domain,
    client_id: options.clientId,
    redirect_uri: options.redirectUri,
  })

  try {
    // If the user is returning to the app after authentication
    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      // handle the redirect and retrieve tokens
      const { appState } = await client.handleRedirectCallback()

      // Notify subscribers that the redirect callback has happened, passing the appState
      // (useful for retrieving any pre-authentication state)
      options.onRedirectCallback(appState)
    }
  } catch (e) {
    state.error = e
  } finally {
    // Initialize our internal authentication state
    state.isAuthenticated = await client.isAuthenticated()
    state.user = await client.getUser()
    
    // Check for existing impersonation session
    if (state.isAuthenticated) {
      await checkImpersonationStatus()
    }
    
    state.loading = false
  }

  return {
    install: (app: App) => {
      app.provide('Auth', authPlugin)
    },
  }
}

interface Auth0Plugin {
  init(options: Auth0PluginOptions): Promise<Plugin>
  routeGuard: NavigationGuardWithThis<undefined>
}

export const Auth0: Auth0Plugin = {
  init,
  routeGuard,
}
